<script setup lang="ts">
import { computed, ref, capitalize } from 'vue'
import { getUserAvatarFallbackBg } from '#shared/lib/user/get-user-avatar-falback-bg'
import { getUserInitials } from '#shared/lib/user/get-user-initials'
import { type BaseUser } from '#shared/model/user'

const { user, size = 'medium' } = defineProps<{ user: BaseUser; size?: 'tiny' | 'small' | 'medium' | 'large' }>()

const alt = computed(() => `${user.displayName}'s avatar`)
const didImageError = ref(false)
const onImageError = () => (didImageError.value = true)
const didImageLoad = ref(false)
const onImageLoad = () => (didImageLoad.value = true)

const isImageDispayed = computed(() => {
  if (!user.avatarUrl) return false
  if (didImageError.value) return false
  return true
})

const initials = computed(() => getUserInitials(user.displayName))
const fallbackBackground = computed(() => getUserAvatarFallbackBg(user.id))

const rootRef = ref<HTMLElement | null>(null)
</script>

<template>
  <div :class="[$style.root, $style['rootSize' + capitalize(size)]]" ref="rootRef">
    <img
      v-if="isImageDispayed"
      :class="[$style.image, { [$style.imageLoading]: !didImageLoad }]"
      :src="user.avatarUrl!"
      :alt="alt"
      @error="onImageError"
      @load="onImageLoad"
    />
    <div
      v-else
      :class="[$style.fallback, $style['fallbackSize' + capitalize(size)]]"
      :style="{ background: fallbackBackground }"
    >
      {{ initials }}
    </div>
  </div>
</template>

<style lang="scss" module>
@use '#mixins/media' as media;

.root {
  display: flex;
  border-radius: 50%;
  flex-shrink: 0;
  overflow: hidden;
}

.rootSizeTiny {
  width: 20px;
  height: 20px;
}

.rootSizeSmall {
  width: 30px;
  height: 30px;

  @include media.mobile {
    width: 25px;
    height: 25px;
  }
}

.rootSizeMedium {
  width: 50px;
  height: 50px;

  @include media.mobile {
    width: 35px;
    height: 35px;
  }
}

.rootSizeLarge {
  width: 140px;
  height: 140px;

  @include media.mobile {
    width: 100px;
    height: 100px;
  }
}

.image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  background-color: rgba($color: #19191b, $alpha: 0.5);
}

.imageLoading {
  opacity: 0;
}

.fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  font-weight: 600;
  color: white;
}

.fallbackSizeTiny {
  font-size: 10px;
}

.fallbackSizeSmall {
  font-size: 14px;

  @include media.mobile {
    font-size: 12px;
  }
}

.fallbackSizeMedium {
  font-size: 20px;

  @include media.mobile {
    font-size: 14px;
  }
}

.fallbackSizeLarge {
  font-size: 56px;

  @include media.mobile {
    font-size: 40px;
  }
}
</style>
